/**
 * Generic sort comparator for table columns.
 *
 * Handles null, boolean, number, and string values with consistent
 * null-last ordering. Pure function — no side effects.
 */
export function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'boolean') {
    return a === b ? 0 : a ? -1 : 1;
  }
  if (typeof a === 'number') {
    return a - (b as number);
  }
  return String(a).localeCompare(String(b));
}

/**
 * Sort an array of objects by a column key with direction.
 *
 * Returns a new array (does not mutate the input).
 */
export function sortByColumn<T extends Record<string, unknown>>(
  items: T[],
  col: string,
  dir: 'asc' | 'desc',
): T[] {
  return [...items].sort((a, b) => {
    const av = a[col];
    const bv = b[col];
    // Null-ness is compared before the direction flip so nulls stay last
    // under both directions, matching SQL's NULLS LAST.
    if (av == null || bv == null) return compareValues(av, bv);
    const cmp = compareValues(av, bv);
    return dir === 'asc' ? cmp : -cmp;
  });
}
